import { onAuthStateChanged } from 'firebase/auth';
import { useEffect, useState } from 'react';

import { useAuth } from '../firebase/FirebaseContext';
import { useAlerts } from './useAlerts';
import { useTotalLikedCount } from './useTotalLikedCount';

// Mirror of HeaderAccountMenu's SIGN_IN_NUDGE_THRESHOLD (the avatar's
// red-dot threshold). Exported so the board-page bell shares one source
// of truth for "anon shopper has invested enough to be worth nudging."
export const SIGN_IN_NUDGE_THRESHOLD = 3;

/**
 * Drives the "You" bottom-tab notification badge. Returns a COUNT of
 * things waiting for the user:
 *
 *   alertCount + (anon && totalLiked >= 3 ? 1 : 0)
 *
 * The sign-in nudge counts as one item because it renders as one banner
 * on the You page. Zero means "nothing waiting" (no badge). Same signal
 * family as the desktop avatar's red dot (`HeaderAccountMenu`), except
 * the tab shows the number — a bare dot didn't communicate that it
 * meant notifications (Kate 2026-07-06).
 *
 * Reuses the existing `useAlerts` + `useTotalLikedCount` subscriptions;
 * both already guard against a missing `ensureAuth` (Storybook/tests), so
 * this hook is safe to call from the tab-bar mount layer.
 */
export function useYouTabBadge(): number {
  const auth = useAuth();
  const [isAnon, setIsAnon] = useState<boolean>(
    () => auth.currentUser?.isAnonymous ?? false,
  );

  useEffect(() => {
    return onAuthStateChanged(auth, (user) => {
      setIsAnon(user?.isAnonymous ?? false);
    });
  }, [auth]);

  const totalLiked = useTotalLikedCount();
  const { count: alertCount } = useAlerts();

  const showSignInNudge = isAnon && totalLiked >= SIGN_IN_NUDGE_THRESHOLD;
  return alertCount + (showSignInNudge ? 1 : 0);
}
